import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Modal, Box, Button } from '@mui/material';
import { vocabularyApi } from '../../api/global';
import AlertModal from '../common/AlertModal';
import ConfirmModalAlertType from '../common/ConfirmAlertType';

function HeaderBtnBox({
  isSaved,
  manualIdx,
  hideTestPaper,
  checkedUnitIdxList = [],
  generatedPaper = [],
  config,
  paperTab,
  setDownloadModalOpen,
  isLoadMode,
  loadedPaperIdx = undefined,
  printPdf,
}) {
  const navigate = useNavigate();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [saveTypeModalOpen, setSaveTypeModalOpen] = useState(false);

  const getApiParams = useCallback(() => ({
    academyName: config.orgName,
    examName: config.paperName,
    logoUrl: config.orgLogoUrl,
    className: config.className,
    teacherName: config.teacherName,
    bookId: manualIdx,
    unitIds: checkedUnitIdxList,
    questions: generatedPaper,
    viewMode: paperTab,
    vocaFirstWordShow: config.vocaFirstWordShow,
    examKorShow: config.examKorShow,
    fontSize: config.fontSize,
    spacing: config.spacing,
    paperDiv: config.paperDiv,
  }), [config, paperTab, generatedPaper, manualIdx, checkedUnitIdxList]);

  const executeSave = useCallback(async (useOverwrite) => {
    try {
      const apiParams = getApiParams();
      let response = null;
      if (useOverwrite) {
        response = await vocabularyApi.updateTestPaper(
          loadedPaperIdx,
          apiParams,
        );
      } else {
        response = await vocabularyApi.saveTestPaper(apiParams);
      }
      if (response.data.message === 'Success') {
        isSaved.current = true;
        setSaveConfirmOpen(true);
      }
    } catch (error) {
      console.error(error);
    }
  }, [isSaved, loadedPaperIdx, getApiParams]);

  const saveTestPaper = useCallback(() => {
    if (isLoadMode) {
      setSaveTypeModalOpen(true);
    } else {
      executeSave(false);
    }
  }, [isLoadMode, executeSave]);

  return (
    <div className="flex gap-2">
      <Button
        variant="darklined"
        sx={{ fontFamily: 'Pretendard', px: '20px' }}
        onClick={saveTestPaper}
        disabled={hideTestPaper}
      >
        저장하기
      </Button>
      <Button
        variant="dark"
        sx={{ fontFamily: 'Pretendard', px: '20px' }}
        onClick={() => setDownloadModalOpen(true)}
        disabled={hideTestPaper}
      >
        다운로드
      </Button>
      <Button
        variant="contained"
        sx={{ fontFamily: 'Pretendard', px: '20px' }}
        onClick={printPdf}
        disabled={hideTestPaper}
      >
        인쇄하기
      </Button>
      <Button
        variant="darklined"
        sx={{ fontFamily: 'Pretendard', px: '20px' }}
        onClick={() => {
          if (
            (generatedPaper?.length >= 1 && hideTestPaper) ||
            !isSaved.current
          ) {
            setConfirmModalOpen(true);
            return;
          } else {
            navigate('/');
          }
        }}
      >
        종료
      </Button>
      <AlertModal
        open={saveConfirmOpen}
        setOpen={setSaveConfirmOpen}
        content="편집한 내용이 저장되었습니다."
        confirmLabel="확인"
        onConfirm={() => setSaveConfirmOpen(false)}
      />
      <Modal
        open={saveTypeModalOpen}
        onClose={() => setSaveTypeModalOpen(false)}
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          minWidth: '351px',
          width: 'auto',
          height: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: '48px 24px 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div className="w-full flex flex-col items-center justify-center text-[14px] font-[400] mb-[16px]">
            <div className="font-[700] mb-[8px]">저장 방식을 선택해주세요.</div>
            <div className="text-[12px] text-[#64748B]">
              덮어쓰기 저장: 기존 시험지에 수정한 내용을 반영합니다.
            </div>
            <div className="text-[12px] text-[#64748B]">
              다른이름으로 저장: 기존 시험지는 유지하고 새 시험지로 저장합니다.
            </div>
          </div>
          <div className="flex w-full items-center justify-center gap-2">
            <Button
              variant="gray"
              onClick={() => setSaveTypeModalOpen(false)}
              sx={{ width: '113px' }}
            >
              취소
            </Button>
            <Button
              variant="gray"
              onClick={() => {
                setSaveTypeModalOpen(false);
                executeSave(false);
              }}
              sx={{ width: '140px' }}
            >
              다른이름으로 저장
            </Button>
            <Button
              variant="dark"
              onClick={() => {
                setSaveTypeModalOpen(false);
                executeSave(true);
              }}
              sx={{ width: '140px' }}
            >
              덮어쓰기 저장
            </Button>
          </div>
        </Box>
      </Modal>
      <ConfirmModalAlertType
        open={confirmModalOpen}
        setOpen={setConfirmModalOpen}
        content={`편집 중인 시험지를 저장하고 교재 종료해주세요.\n종료하시겠습니까?`}
        cancelLabel="취소"
        confirmLabel="종료"
        onCancel={() => setConfirmModalOpen(false)}
        onConfirm={() => navigate('/')}
      />
    </div>
  );
}

HeaderBtnBox.propTypes = {
  isSaved: PropTypes.object,
  manualIdx: PropTypes.number,
  hideTestPaper: PropTypes.bool,
  unitList: PropTypes.array,
  checkedUnitIdxList: PropTypes.array,
  generatedPaper: PropTypes.array,
  config: PropTypes.object,
  paperTab: PropTypes.string,
  setDownloadModalOpen: PropTypes.func.isRequired,
  isLoadMode: PropTypes.bool.isRequired,
  loadedPaperIdx: PropTypes.number,
  printPdf: PropTypes.func.isRequired,
};

export default React.memo(HeaderBtnBox);
